/**
 * useDefaultCurrency Hook
 * 
 * Fetches default currency from workspace settings and subscribes to changes.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { auth, db } from "../../../firebase";
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { WorkspaceSettings } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

// Import ISO-4217 currencies
import { ISO4217_CURRENCIES, getCurrencyByCode, isValidISO4217Code } from "../../../utils/iso4217Currencies";

// Common currencies list - now uses complete ISO-4217 list
export const COMMON_CURRENCIES: readonly { code: string; name: string; symbol: string }[] = ISO4217_CURRENCIES.map((c) => ({
  code: c.code,
  name: c.name,
  symbol: c.symbol,
}));

// Export helper functions
export { getCurrencyByCode, isValidISO4217Code };

export interface UseDefaultCurrencyParams {
  workspaceId: string;
}

export interface UseDefaultCurrencyResult {
  defaultCurrency: string | null;
  setDefaultCurrency: React.Dispatch<React.SetStateAction<string | null>>;
  isLoading: boolean;
}

/**
 * Hook for fetching default currency from settings
 */
export const useDefaultCurrency = ({ workspaceId }: UseDefaultCurrencyParams): UseDefaultCurrencyResult => {
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!workspaceId || (!auth.currentUser && !isDemoMode())) {
      setIsLoading(false);
      return;
    }

    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as WorkspaceSettings;
          setDefaultCurrency(data.defaultCurrency || "USD");
        } else {
          // For existing workspaces, default to USD
          setDoc(settingsRef, { defaultCurrency: "USD" }, { merge: true });
          setDefaultCurrency("USD");
          // Also create USD currency if it doesn't exist
          const usdCurrencyRef = doc(db, "workspaces", workspaceId, "currencies", "USD");
          getDoc(usdCurrencyRef).then((usdDoc) => {
            if (!usdDoc.exists()) {
              const usdInfo = COMMON_CURRENCIES.find((c) => c.code === "USD");
              if (usdInfo) {
                setDoc(usdCurrencyRef, {
                  name: usdInfo.name,
                  symbol: usdInfo.symbol,
                  rate: 1.0,
                  updatedAt: serverTimestamp(),
                });
              }
            }
          });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching settings:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  return {
    defaultCurrency,
    setDefaultCurrency,
    isLoading,
  };
};
